/**
 * Batch QR Code Generator
 *
 * This module provides functionality for generating multiple QR codes in batch.
 * It generates QR codes for all bays (fixed and super) plus the site-level QR,
 * applying the current template settings to all generated codes.
 *
 * Requirements: 8.2, 8.3, 8.7
 * - 8.2: Generate QR codes for all bays (fixed and super) plus the site-level QR
 * - 8.3: Apply the current template settings to all generated QR codes
 * - 8.7: If any individual QR code fails to generate, continue with the others and report failures
 *
 * Properties validated:
 * - Property 9: Batch Generation Coverage - produces exactly one QR code for each bay
 * - Property 11: Batch Error Isolation - failures don't block other generations
 */

import type { PreviewTarget, QrTemplate } from '../types';
import type { AdvancedOptions } from '../types/advancedOptions';
import { DEFAULT_ADVANCED_OPTIONS } from '../types/advancedOptions';
import { generateSiteQrUrlWithAdvanced, generateBayQrUrlWithAdvanced } from './advancedUrlGenerator';
import { generateDownloadFilename } from './filenameGenerator';
import { generateQrMatrix, calculateOverlaySize } from '../generator/qrGenerator';
import { renderQrToDataUrl } from '../generator/canvasRenderer';
import { getEffectiveTemplate, type PreviewConfig } from './templateModifier';
import { embedPngMetadata, buildQrMetadata } from './pngMetadata';

/**
 * Default QR code output size in pixels
 * Provides sufficient resolution for print use (300 DPI equivalent at ~3.5 inches)
 */
export const DEFAULT_QR_SIZE = 1024;

/**
 * Result of a successful QR code generation
 */
export interface BatchQrResult {
  readonly filename: string;
  readonly dataUrl: string;
}

/**
 * Result of a failed QR code generation
 */
export interface BatchQrFailure {
  readonly target: PreviewTarget;
  readonly error: string;
}

/**
 * Complete result of batch QR code generation
 */
export interface BatchGenerationResult {
  readonly successful: readonly BatchQrResult[];
  readonly failed: readonly BatchQrFailure[];
}

/**
 * Callback for tracking batch generation progress
 * @param completed - Number of QR codes generated so far
 * @param total - Total number of QR codes to generate
 */
export interface BatchProgressCallback {
  (completed: number, total: number): void;
}

/**
 * Fixed bay input for batch generation
 */
export interface FixedBayInput {
  readonly bayId: string;
  readonly bayNumber: string;
}

/**
 * Super bay input for batch generation
 */
export interface SuperBayInput {
  readonly bayId: string;
  readonly bayName: string;
}

/**
 * Generates a single QR code for a given target
 *
 * @param template - The QR template with customization settings
 * @param siteId - The site identifier
 * @param siteName - The site name for filename generation
 * @param target - The preview target (site, fixed-bay, or super-bay)
 * @param siteKey - The site key for URL validation
 * @param advancedOptions - Advanced options for URL generation
 * @param author - The user name/email for metadata embedding
 * @param size - The output size in pixels
 * @returns Promise resolving to the QR result or throwing an error
 */
async function generateSingleQrCode(
  template: QrTemplate,
  siteId: string,
  siteName: string,
  target: PreviewTarget,
  siteKey: string,
  advancedOptions: AdvancedOptions,
  author: string,
  size: number = DEFAULT_QR_SIZE
): Promise<BatchQrResult> {
  // Generate the URL based on target type with advanced options
  const url =
    target.type === 'site'
      ? generateSiteQrUrlWithAdvanced(siteId, siteKey, advancedOptions)
      : generateBayQrUrlWithAdvanced(siteId, target.bayId, siteKey, advancedOptions);

  // Build preview config for template modifier
  const previewConfig: PreviewConfig = target.type === 'site'
    ? { type: 'site', siteName }
    : target.type === 'fixed-bay'
      ? { type: 'fixed-bay', bayId: target.bayId, bayNumber: target.bayNumber }
      : { type: 'super-bay', bayId: target.bayId, bayName: target.bayName };

  // Apply effective template transformation (respects showBayLabel setting)
  const effectiveTemplate = getEffectiveTemplate(template, previewConfig);

  // Generate the QR matrix
  const matrix = generateQrMatrix(url, 'H');

  // Calculate overlay size if center customization is enabled
  const overlaySize = effectiveTemplate.centerEnabled ? calculateOverlaySize(size) : 0;

  // Determine bay number for center label (only when showCenterLabel is true)
  const bayNumber = effectiveTemplate.showCenterLabel
    ? previewConfig.bayNumber
    : undefined;

  // Determine logo (only show if effective template says so)
  const logoDataUrl = effectiveTemplate.showLogo
    ? effectiveTemplate.logoDataUrl
    : null;

  // Render the QR code to a data URL
  const dataUrl = await renderQrToDataUrl(matrix, size, effectiveTemplate.color, {
    centerEnabled: effectiveTemplate.centerEnabled,
    overlaySize,
    logoDataUrl,
    bayNumber,
    fontFamily: effectiveTemplate.fontFamily,
    fontSize: effectiveTemplate.fontSize,
    bayLabelColor: effectiveTemplate.bayLabelColor,
    centerPadding: effectiveTemplate.centerPadding,
    cornerLabelText: effectiveTemplate.cornerLabelText,
    moduleCount: matrix.size,
    bottomLogoEnabled: effectiveTemplate.bottomLogo,
  });

  // Build metadata title based on target type
  const metadataTitle =
    target.type === 'site'
      ? `${siteName} - Site QR`
      : target.type === 'fixed-bay'
        ? `${siteName} - Bay ${target.bayNumber}`
        : `${siteName} - ${target.bayName}`;

  // Embed PNG metadata
  const metadata = buildQrMetadata(metadataTitle, url, author);
  const dataUrlWithMetadata = embedPngMetadata(dataUrl, metadata);

  // Generate the filename
  const filename = generateDownloadFilename(siteName, target);

  return {
    filename,
    dataUrl: dataUrlWithMetadata,
  };
}

/**
 * Generates QR codes for all bays in batch
 *
 * This function generates QR codes for:
 * 1. The site-level QR code
 * 2. All fixed bays (with bay numbers in center overlay when enabled)
 * 3. All super bays
 *
 * Property 9: Batch Generation Coverage
 * Produces exactly one QR code for each bay, and each fixed bay QR code
 * includes the bay number in the center overlay when center customization is enabled.
 *
 * Property 11: Batch Error Isolation
 * If any individual QR code fails to generate, the system continues generating
 * remaining bays and the final result includes all successful generations plus
 * a list of failed targets.
 *
 * @param template - The QR template with customization settings
 * @param siteId - The site identifier
 * @param siteName - The site name for filename generation
 * @param fixedBays - Array of fixed bays with bayId and bayNumber
 * @param superBays - Array of super bays with bayId and bayName
 * @param siteKey - The site key for URL validation (Requirement 3.1)
 * @param onProgress - Optional callback for progress updates
 * @param advancedOptions - Optional advanced options for URL generation
 * @param author - The user name/email for metadata embedding (defaults to 'Unknown')
 * @returns Promise resolving to the batch generation result
 *
 * Requirements: 8.2, 8.3, 8.7, 3.1, 3.2, 6.1, 6.4
 */
export async function generateBatchQrCodes(
  template: QrTemplate,
  siteId: string,
  siteName: string,
  fixedBays: readonly FixedBayInput[],
  superBays: readonly SuperBayInput[],
  siteKey: string,
  onProgress?: BatchProgressCallback,
  advancedOptions?: AdvancedOptions,
  author: string = 'Unknown'
): Promise<BatchGenerationResult> {
  const successful: BatchQrResult[] = [];
  const failed: BatchQrFailure[] = [];

  // Use default advanced options if not provided
  const effectiveAdvancedOptions = advancedOptions ?? DEFAULT_ADVANCED_OPTIONS;

  // Build the list of all targets to generate
  const targets: PreviewTarget[] = [
    // Site-level QR code
    { type: 'site' },
    // Fixed bays
    ...fixedBays.map(
      (bay): PreviewTarget => ({
        type: 'fixed-bay',
        bayId: bay.bayId,
        bayNumber: bay.bayNumber,
      })
    ),
    // Super bays
    ...superBays.map(
      (bay): PreviewTarget => ({
        type: 'super-bay',
        bayId: bay.bayId,
        bayName: bay.bayName,
      })
    ),
  ];

  const total = targets.length;
  let completed = 0;

  // Report initial progress
  if (onProgress) {
    onProgress(completed, total);
  }

  // Generate QR codes for each target
  // Process sequentially to avoid overwhelming the browser
  for (const target of targets) {
    try {
      const result = await generateSingleQrCode(
        template,
        siteId,
        siteName,
        target,
        siteKey,
        effectiveAdvancedOptions,
        author
      );
      successful.push(result);
    } catch (error) {
      // Property 11: Batch Error Isolation
      // Continue with remaining bays and track the failure
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      failed.push({
        target,
        error: errorMessage,
      });
    }

    // Update progress
    completed++;
    if (onProgress) {
      onProgress(completed, total);
    }
  }

  return {
    successful,
    failed,
  };
}
